import { createTheme, ThemeOptions } from "@mui/material/styles";

const primaryColor = "#1A4480";
const primaryLightColor = "#ACC8DF";
const secondaryColor = "#D9E8F6";

const PFATheme: ThemeOptions = createTheme({
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": {
            textDecoration: "underline",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "marginTop": 12,
          "textTransform": "none",
          "fontSize": ".875rem",
          "height": 32,
          "transition": "none",
          "margin": 0,
          "&.MuiButton-outlinedPrimary": {
            "&:hover, &.Mui-focusVisible": {
              backgroundColor: secondaryColor,
              borderColor: primaryColor,
            },
          },
          "&.MuiButton-colorInherit": {
            "margin": "0 0 1rem 0",
            "backgroundColor": "#ffffff",
            "border": `1px solid ${primaryLightColor}`,
            "color": primaryColor,
            "boxShadow": "none",
            " &:hover, &.Mui-focusVisible": {
              boxShadow: "none",
              backgroundColor: secondaryColor,
            },
            "&:active": {
              backgroundColor: primaryColor,
              color: "#ffffff",
            },
          },
          "&.MuiButton-containedPrimary": {
            "margin": "0 0 2rem 0",
            "&:hover, &.Mui-focusVisible": {
              border: `1px solid ${primaryLightColor}`,
              color: primaryColor,
              backgroundColor: secondaryColor,
              "& span": {
                color: primaryColor,
              },
            },
            "&:active": {
              backgroundColor: primaryColor,
              color: "#ffffff",
              "& span": {
                color: "#ffffff",
              },
            },
          },
          "&.MuiButton-containedSecondary": {
            "margin": "0 0 1rem 0",
            "border": `1px solid ${primaryLightColor}`,
            "color": primaryColor,
            "boxShadow": "none",
            " &:hover, &.Mui-focusVisible": {
              boxShadow: "none",
              backgroundColor: "#ffffff",
            },
            "&:disabled": {
              color: "rgba(0, 0, 0, 0.26)",
            },
            "&:active": {
              backgroundColor: primaryColor,
              color: "#ffffff",
            },
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": {
            backgroundColor: "rgba(0, 0, 0, 0.08)",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          "&:focus": {
            backgroundColor: "#FFFFFF",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "backgroundColor": "#FFFFFF",
          "color": primaryColor,
          "borderRadius": 4,
          "& input.Mui-disabled": { backgroundColor: secondaryColor, color: primaryColor, "WebkitTextFillColor": "inherit" },
          "& select.Mui-disabled": { backgroundColor: secondaryColor, color: primaryColor, "WebkitTextFillColor": "inherit" },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${primaryLightColor}`,
          },
          "&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${primaryLightColor}`,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: primaryColor,
          },
        },
        notchedOutline: {
          borderColor: "#ACC8DF",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          //removes dotted focus created by FDIC Header
          outline: "0 !important",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: primaryColor,
          backgroundColor: "#FFFFFF",
          padding: "2px 10px 0 10px",
          marginLeft: -5,
          marginTop: -2,
          border: 0,
          borderRadius: 3,
        },
        outlined: {
          "&.MuiInputLabel-marginDense": {
            transform: "translate(9px, 12px) scale(1)",
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -6px) scale(.75)",
          },
        },
        shrink: {
          color: primaryColor,
          fontSize: "1rem",
          backgroundColor: "#fff",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontFamily:
            '"Merriweather Web", Georgia, Cambria, "Times New Roman", Times, serif',
          fontSize: "2rem",
          fontWeight: "normal",
          color: primaryColor,
          padding: 0,
          marginBottom: "2rem",
        },
        h2: {
          fontFamily: "'Source Sans Pro', sans-serif",
          fontSize: "1.5rem",
          fontWeight: "bold",
          color: primaryColor,
          padding: 0,
          paddingBottom: "1rem",
          lineHeight: "1.5rem",
        },
        h3: {
          fontFamily: "'Source Sans Pro', sans-serif",
          fontSize: "1.125rem",
          fontWeight: "bold",
          color: primaryColor,
          padding: 0,
          paddingBottom: "1rem",
        },
        h4: {
          fontFamily: "'Source Sans Pro', sans-serif",

          fontSize: "1rem",
          fontWeight: "bold",
          color: primaryColor,
          lineHeight: "1.5rem",
          padding: 0,
          paddingBottom: "1rem",
        },
        body1: {
          "fontFamily": "'Source Sans Pro', sans-serif",
          "fontSize": ".875rem",
          "color": "#333333",
          "lineHeight": "1.5rem",
          "padding": 0,
          "& > a": {
            "color": "#0033FF",
            "textDecoration": "underline",
            "&:visited": {
              color: "#9900CC",
            },
            "&:focus, &:hover": {
              color: "#BB3300",
            },
            "&:active": {
              color: "#663300",
            },
          },
        },
        body2: {
          "fontFamily": "'Source Sans Pro', sans-serif",
          "fontSize": "1rem",
          "color": "#333333",
          "lineHeight": "1.5rem",
          "padding": 0,
          "paddingBottom": "1rem",
          "& > a": {
            "color": "#0033FF",
            "textDecoration": "underline",
            "padding": 3,
            "margin": -3,
            "borderRadius": 3,
            "&:visited": {
              color: "#9900CC",
            },
            "&:focus, &:hover": {
              color: "#BB3300",
              backgroundColor: "#E6F3FF",
            },
            "&:active": {
              color: "#ffffff",
              backgroundColor: primaryColor,
              textDecoration: "none",
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: `0px 5px 5px -3px rgba(172,200,223,1), 0px 8px 10px 1px rgba(172,200,223,1), 0px 3px 14px 2px rgba(172,200,223,1)`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: 8,
        },
      },
    },
  },
  typography: {
    fontFamily: [
      '"Source Sans Pro"',
      '"Helvetica Neue"',
      "Helvetica",
      "Roboto",
      "Arial",
      "sans-serif",
    ].join(","),
    h1: {
      color: `rgb(85, 85, 85)`,
      fontWeight: 700,
      fontSize: 32,
    },
    h4: {
      fontWeight: 700,
      fontSize: 24,
    },
    h6: {
      fontWeight: 700,
      fontSize: 18,
    },
    h2: {
      padding: "24px 0px",
      lineHeight: "32px",
      fontWeight: 700,
      fontSize: "32px",
    },
    h3: {
      padding: "24px 0px",
      lineHeight: "24px",
      fontWeight: 700,
      fontSize: "24px",
    },
  },
  palette: {
    primary: {
      main: primaryColor,
      light: primaryLightColor,
    },
    secondary: {
      main: secondaryColor,
      dark: "#8FA6D6",
    },
    error: {
      main: "#CC0000",
    },
  },
});
export default PFATheme;
